/**
 * Node - Represents a single node in the network with snake-like movement
 */

export enum GrowthState {
  GROW_STRAIGHT = 'GROW_STRAIGHT',
  BRANCH_LEFT = 'BRANCH_LEFT',
  BRANCH_RIGHT = 'BRANCH_RIGHT',
  BRANCH_BOTH = 'BRANCH_BOTH',
  PAUSE = 'PAUSE',
}

export class Node {
  public x: number
  public y: number
  public angle: number
  public id: string | null
  public isActive: boolean
  public isOrigin: boolean
  public hasReachedTarget: boolean
  public age: number
  public speed: number
  public vx: number
  public vy: number
  public state: GrowthState
  public targetAngle: number
  public turnRate: number
  public stateAge: number // How long in current state
  public birthTime: number // For color pulsing on new nodes

  constructor(x: number, y: number, angle: number, id: string | null = null) {
    this.x = x
    this.y = y
    this.angle = angle
    this.id = id
    this.isActive = false
    this.isOrigin = false
    this.hasReachedTarget = false
    this.age = 0
    this.speed = 2 + Math.random() * 2 // 2-4 px per frame
    this.vx = 0
    this.vy = 0
    this.state = GrowthState.GROW_STRAIGHT
    this.targetAngle = angle
    this.turnRate = 0.15 // Gradual turning creates curves
    this.stateAge = 0
    this.birthTime = Date.now()
  }

  /**
   * Normalize angle to -PI to PI range
   */
  private normalizeAngle(angle: number): number {
    while (angle > Math.PI) angle -= Math.PI * 2
    while (angle < -Math.PI) angle += Math.PI * 2
    return angle
  }

  /**
   * Update angle gradually toward target (creates smooth curves)
   */
  updateAngleTowardTarget(targetX: number, targetY: number): void {
    // Calculate desired angle toward target
    this.targetAngle = Math.atan2(targetY - this.y, targetX - this.x)

    // Gradually turn toward target (creates curves instead of straight lines!)
    const angleDiff = this.normalizeAngle(this.targetAngle - this.angle)
    this.angle += angleDiff * this.turnRate

    // Normalize current angle
    this.angle = this.normalizeAngle(this.angle)
  }

  /**
   * Apply state-based angle adjustment (snake-like curving)
   */
  applyStateMovement(): void {
    switch (this.state) {
      case GrowthState.GROW_STRAIGHT:
        // Gradual turn toward target (already handled in updateAngleTowardTarget)
        break

      case GrowthState.BRANCH_LEFT:
        // Curve 30° left
        this.angle -= Math.PI / 6
        break

      case GrowthState.BRANCH_RIGHT:
        // Curve 30° right
        this.angle += Math.PI / 6
        break

      case GrowthState.PAUSE:
        // Don't move this frame (50% chance)
        if (Math.random() < 0.5) {
          return // Skip movement
        }
        break

      case GrowthState.BRANCH_BOTH:
        // Handled specially in growth logic (spawns two children)
        break
    }

    // Move in current direction
    this.x += Math.cos(this.angle) * this.speed
    this.y += Math.sin(this.angle) * this.speed
  }

  /**
   * Update node position based on target (with snake-like movement)
   */
  updateTowardTarget(targetX: number, targetY: number): void {
    const dx = targetX - this.x
    const dy = targetY - this.y
    const distance = Math.sqrt(dx * dx + dy * dy)

    if (distance < 10) {
      this.hasReachedTarget = true
      this.isActive = false
      return
    }

    // Update angle gradually toward target
    this.updateAngleTowardTarget(targetX, targetY)

    // Apply state-based movement (creates curves!)
    this.applyStateMovement()
  }

  /**
   * Update node position based on angle (for organic growth)
   */
  updateWithAngle(): void {
    this.x += Math.cos(this.angle) * this.speed
    this.y += Math.sin(this.angle) * this.speed
  }

  /**
   * Update age
   */
  incrementAge(): void {
    this.age++
  }
}
